import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, take } from 'rxjs';
import { DIRECTORY_ROLE_NAME } from '@shared/models/directory-role.model';
import { UserRole } from '@shared/models/user-role.model';
import { AUTH_CLAIM, AuthUser } from '../models/auth-user.model';
import {
  AppModule,
  AppSession,
  MODULE_ACTION,
  ModuleAccess,
  ModuleAction,
  SESSION_STATUS,
  SessionStatus,
  SessionWarehouse,
} from '../models/session.model';

@Injectable({ providedIn: 'root' })
export class AuthStateService {
  //* Services
  private readonly _auth0 = inject(AuthService);

  //* Signals
  private readonly _isAuthenticated = toSignal(this._auth0.isAuthenticated$, {
    initialValue: false,
  });
  private readonly _isLoading = toSignal(this._auth0.isLoading$, { initialValue: true });
  private readonly _profile = toSignal(this._auth0.user$, { initialValue: null });
  private readonly _error = toSignal(this._auth0.error$, { initialValue: null });
  private readonly _session = signal<AppSession | null>(null);

  //* Computed
  public readonly isAuthenticated = computed(() => this._isAuthenticated());
  public readonly isLoading = computed(() => this._isLoading());
  public readonly errorMessage = computed(() => this._error()?.message ?? null);
  public readonly session = this._session.asReadonly();

  public readonly displayName = computed<string>(
    () => this._session()?.displayName || this.user()?.name || '',
  );

  public readonly roles = computed<UserRole[]>(
    () => this._stringArrayClaim(AUTH_CLAIM.ROLES) as UserRole[],
  );

  public readonly permissions = computed<string[]>(() => {
    const fromClaim = this._stringArrayClaim(AUTH_CLAIM.PERMISSIONS);
    const fromSession = this._session()?.permissions ?? [];

    return [...new Set([...fromClaim, ...fromSession])];
  });

  public readonly modules = computed<ModuleAccess[]>(() => this._session()?.modules ?? []);

  public readonly subject = computed<string>(() => this._session()?.subject ?? '');

  public readonly roleLabel = computed<string>(() => this._session()?.role?.name ?? '');

  public readonly warehouse = computed<SessionWarehouse | null>(
    () => this._session()?.warehouse ?? null,
  );

  public readonly warehouseLabel = computed<string | null>(() => {
    const warehouse = this.warehouse();
    if (!warehouse) return null;

    return warehouse.name ? `${warehouse.name} (${warehouse.code})` : warehouse.code || null;
  });

  public readonly status = computed<SessionStatus | null>(() => this._session()?.status ?? null);

  public readonly isBlocked = computed<boolean>(() => this.status() === SESSION_STATUS.BLOCKED);

  public readonly hasWebAccess = computed<boolean>(() => this._session()?.hasWebAccess ?? true);

  public readonly hasRole = computed<boolean>(() => this._session()?.role !== null);

  public readonly isAdministrator = computed<boolean>(
    () => this._session()?.role?.name.trim().toLowerCase() === DIRECTORY_ROLE_NAME.ADMIN,
  );

  public readonly isAuthorizationResolved = computed<boolean>(
    () => this.permissions().length > 0 || this._session() !== null,
  );

  public readonly user = computed<AuthUser | null>(() => {
    const profile = this._profile();
    if (!profile) return null;

    return {
      name: profile.name ?? profile.nickname ?? profile.email ?? '',
      email: profile.email ?? '',
    };
  });

  //* Public Methods
  public hasPermission(...required: string[]): boolean {
    if (required.length === 0) return true;
    if (!this.isAuthorizationResolved()) return true;

    const granted = this.permissions();
    return required.some((permission) => granted.includes(permission));
  }

  public hasExplicitPermission(permission: string): boolean {
    const session = this._session();
    return session !== null && session.permissions.includes(permission);
  }

  public hasModuleAccess(module: AppModule, action: ModuleAction = MODULE_ACTION.READ): boolean {
    if (!this.isAuthorizationResolved()) return true;

    const entry = this.modules().find((moduleAccess) => moduleAccess.code === module);
    return entry?.actions.includes(action) ?? false;
  }

  public canWrite(module: AppModule): boolean {
    return this.hasModuleAccess(module, MODULE_ACTION.WRITE);
  }

  public isAuthenticatedOnceResolved(): Observable<boolean> {
    return this._auth0.isAuthenticated$.pipe(take(1));
  }

  public getAccessToken(options?: { forceRefresh?: boolean }): Observable<string> {
    return this._auth0.getAccessTokenSilently(
      options?.forceRefresh ? { cacheMode: 'off' } : undefined,
    );
  }

  public signIn(targetUrl = '/'): void {
    this._auth0.loginWithRedirect({ appState: { target: targetUrl } });
  }

  public signOut(): void {
    this._auth0.logout({ logoutParams: { returnTo: this._returnToUrl() } });
  }

  public setSession(session: AppSession | null): void {
    this._session.set(session);
  }

  public clearSession(): void {
    this._session.set(null);
  }

  //* Private Methods
  private _stringArrayClaim(claim: string): string[] {
    const value = this._profile()?.[claim];
    if (!Array.isArray(value)) return [];

    return value.filter((entry): entry is string => typeof entry === 'string');
  }

  private _returnToUrl(): string {
    return window.location.origin;
  }
}
